import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

function Showcase() {
  const [jobs, setJobs] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch("/api/jobs");
        if (!response.ok) throw new Error("Failed to fetch jobs");
        const data = await response.json();

        // Handle both old and new API response formats
        const jobsArray = Array.isArray(data) ? data : data.jobs || [];
        setJobs(jobsArray || []);

        // Extract unique tags from all jobs
        const tagsSet = new Set();
        jobsArray.forEach((job) => {
          if (job.tags && Array.isArray(job.tags)) {
            job.tags.forEach((tag) => tagsSet.add(tag));
          }
        });
        setAllTags(Array.from(tagsSet).sort());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const filtered = jobs.filter((j) => {
    const matchesSearch =
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.description.toLowerCase().includes(search.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => j.tags && j.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
      {/* Hero */}
      <div className="pt-32 pb-16 text-center animate-fade-in px-4">
        <h1 className="text-6xl font-bold mb-4">
          Our{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Showcase
          </span>
        </h1>
        <p className="text-xl text-gray-300 max-w-xl mx-auto mb-8">
          A collection of projects we're proud of — each one built with care,
          skill, and attention to detail.
        </p>

        {/* Search bar */}
        <div className="relative max-w-md mx-auto mb-8">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:shadow-lg transition-all duration-300"
          />
        </div>

        {/* Tag filters */}
        {allTags.length > 0 && (
          <div className="max-w-4xl mx-auto mb-8">
            <p className="text-gray-300 text-sm mb-3 font-semibold">
              Filter by tags:
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    selectedTags.includes(tag)
                      ? "bg-cyan-500 text-white"
                      : "bg-white bg-opacity-10 text-gray-300 hover:bg-opacity-20"
                  }`}
                >
                  #{tag}
                </button>
              ))}
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-white bg-opacity-10 text-gray-300 hover:bg-opacity-20 transition-all duration-300 italic"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mb-4"></div>
            <p className="text-gray-400">Loading projects…</p>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20">
            <BuildingOfficeIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-300 text-xl mb-2">
              {search ? "No projects match your search." : "No projects yet."}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-4 text-cyan-400 underline hover:text-white transition"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            <p className="text-gray-400 text-sm mb-8">
              Showing {filtered.length} project
              {filtered.length !== 1 ? "s" : ""}
              {search && ` for "${search}"`}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((job, idx) => (
                <Link
                  key={job.id}
                  to={`/job/${job.slug}`}
                  className="group block bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl overflow-hidden hover:bg-opacity-10 hover:border-cyan-400 hover:border-opacity-50 transition-all duration-300 hover-card animate-fade-in"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden h-52">
                    {job.images && job.images.length > 0 ? (
                      <img
                        src={job.images[0].image_path}
                        alt={job.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full bg-gradient-to-br from-blue-700 to-cyan-600 flex items-center justify-center"
                      style={{
                        display:
                          job.images && job.images.length > 0 ? "none" : "flex",
                      }}
                    >
                      <BuildingOfficeIcon className="w-16 h-16 text-white" />
                    </div>
                    {/* Image count badge */}
                    {job.images && job.images.length > 1 && (
                      <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
                        {job.images.length} images
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                      {job.title}
                    </h3>
                    <p className="text-gray-200 text-sm mb-4 line-clamp-2">
                      {job.description}
                    </p>
                    {/* Tags */}
                    {job.tags && job.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {job.tags.map((tag, tidx) => (
                          <span
                            key={tidx}
                            className="text-xs bg-cyan-500 bg-opacity-20 text-cyan-300 px-2 py-1 rounded"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <CheckIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {new Date(job.completion_date).toLocaleDateString(
                          "en-CA",
                          {
                            year: "numeric",
                            month: "short",
                          },
                        )}
                      </p>
                      <span className="text-cyan-400 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300 inline-block">
                        View Project →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      {/* CTA */}
      <div className="border-t border-white border-opacity-10 py-16 text-center px-4">
        <h2 className="text-3xl font-bold mb-4">Have a Project in Mind?</h2>
        <p className="text-gray-200 mb-8 max-w-md mx-auto">
          Let's talk about what you're looking to build.
        </p>
        <Link
          to="/contact"
          className="inline-block bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-10 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300"
        >
          Get in Touch →
        </Link>
      </div>
    </div>
  );
}

export default Showcase;
